/**
 * HomePage — the storefront landing screen, driven by LIVE data.
 *
 * The page is a stack of rails a student scrolls through. Three of them are
 * COMPUTED from real data rather than curated by hand:
 *
 *   Most loved   — units actually sold across paid orders on this campus
 *   Best offers  — the biggest MRP-to-price gaps in stock right now
 *   Under ₹50    — cheapest first, for a student on an allowance
 *
 * Everything after that is one rail per CATEGORY the college's admin created.
 * That is deliberate: seasonal sections like "Summer Essentials" or "Winter
 * Warmers" appear the moment an admin adds that category and stock it — no
 * code change, no deploy, and each campus curates its own. Hardcoding the
 * seasons here would mean every college got the same ones, in the wrong
 * hemisphere half the time.
 *
 * Loading → skeletons, empty → a friendly message, error → retry.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, PackageOpen, Flame, BadgePercent, Wallet, Copy, Check } from 'lucide-react';
import { formatCurrency } from '@rocket07/shared';
import { Container } from '@/components/layout/Container';
import { ProductCard } from '@/components/product/ProductCard';
import { ProductCardSkeleton, EmptyState, ErrorState } from '@/components/ui';
import { useCollege } from '@/context/CollegeContext';
import {
  useProducts,
  useCategories,
  useCoupons,
  useTopSelling,
  useCategoryRails,
} from '@/hooks/useStorefront';
import { ROUTES } from '@/constants/routes';

/** Student-budget threshold for the cheap-picks rail. */
const BUDGET_LIMIT = 50;

export function HomePage() {
  const { collegeId, college } = useCollege();
  const { data, isLoading, isError, error, refetch } = useProducts({ collegeId, limit: 30 });
  const { data: categories = [] } = useCategories(collegeId);
  const { data: coupons = [] } = useCoupons(collegeId);
  const { data: topSelling = [] } = useTopSelling(collegeId);
  const categoryRails = useCategoryRails(collegeId, categories);

  const products = data?.items ?? [];

  const featured = products.slice(0, 12);

  const bestOffers = [...products]
    .filter((p) => p.mrp > p.price)
    .sort((a, b) => b.mrp - b.price - (a.mrp - a.price))
    .slice(0, 6);

  /**
   * Cheap picks. NOT a "new arrivals" rail: the API already returns
   * newest-first, so that would have been the same products as "Fresh at…"
   * under a second heading. Price is a genuinely different axis, and the one
   * a student on an allowance actually sorts by.
   */
  const budgetBuys = [...products]
    .filter((p) => p.price <= BUDGET_LIMIT)
    .sort((a, b) => a.price - b.price)
    .slice(0, 6);

  return (
    <Container className="pt-4">
      {isError ? (
        <ErrorState error={error} onRetry={refetch} className="min-h-[50vh]" />
      ) : isLoading ? (
        <Rail title="Loading…">
          {Array.from({ length: 6 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </Rail>
      ) : products.length === 0 ? (
        <EmptyState
          icon={PackageOpen}
          title="No products yet"
          description={`${college?.name ?? 'This campus'} hasn't added products yet. Check back soon!`}
          className="min-h-[55vh]"
        />
      ) : (
        <>
          <Rail title={`Fresh at ${college?.name ?? 'your campus'}`} products={featured} />

          {/* Coupons for THIS campus — set by its admin, right after the fresh rail. */}
          {coupons.length > 0 && <CouponsRow coupons={coupons} />}

          {/* Hidden until this campus has actually sold something — an empty
              "Most loved" is worse than no rail at all. */}
          {topSelling.length > 0 && (
            <Rail title="Most loved on campus" icon={Flame} products={topSelling.slice(0, 6)} />
          )}

          {bestOffers.length > 0 && (
            <Rail title="Best offers" icon={BadgePercent} products={bestOffers} />
          )}

          {budgetBuys.length > 0 && (
            <Rail title={`Under ${formatCurrency(BUDGET_LIMIT)}`} icon={Wallet} products={budgetBuys} />
          )}

          {categoryRails.map(({ category, products: railProducts, isLoading: railLoading }) => (
            <Rail
              key={category._id}
              title={category.name}
              to={`${ROUTES.categories}?category=${category._id}`}
              products={railProducts}
            >
              {railLoading
                ? Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)
                : null}
            </Rail>
          ))}

          <p className="mt-8 mb-2 text-center text-xs text-muted">
            That&apos;s everything {college?.name ?? 'your campus'} stocks today.
          </p>
        </>
      )}
    </Container>
  );
}

function Rail({ title, icon: Icon, products, to, children }) {
  return (
    <section className="mt-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-lg font-extrabold tracking-tight text-ink">
          {Icon && <Icon size={18} className="text-primary-600" />}
          {title}
        </h2>
        <Link
          to={to ?? ROUTES.categories}
          className="inline-flex items-center gap-0.5 rounded-full bg-tile px-3 py-1 text-xs font-semibold text-ink"
        >
          View all <ChevronRight size={14} />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-x-2.5 gap-y-5 sm:grid-cols-4 lg:grid-cols-6">
        {children ?? products.map((product) => <ProductCard key={product.id} product={product} />)}
      </div>
    </section>
  );
}

/* -------------------------------- Coupons -------------------------------- */

/** The campus's active coupons, as a horizontal strip of ticket cards. */
function CouponsRow({ coupons }) {
  return (
    <section className="mt-5">
      <div className="mb-3 flex items-center gap-1.5">
        <BadgePercent size={18} className="text-primary-600" />
        <h2 className="text-lg font-extrabold tracking-tight text-ink">Offers for you</h2>
      </div>
      <div className="no-scrollbar -mb-1 flex gap-3 overflow-x-auto pb-1">
        {coupons.map((coupon) => (
          <CouponCard key={coupon._id} coupon={coupon} />
        ))}
      </div>
    </section>
  );
}

function CouponCard({ coupon }) {
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the code is still visible to type manually */
    }
  };

  return (
    <div className="flex w-72 shrink-0 flex-col justify-between rounded-2xl border border-success/50 bg-white p-4 transition-shadow duration-200 hover:shadow-md">
      {/* Offer headline */}
      <p className="text-lg font-extrabold leading-tight text-success">{coupon.label}</p>
      {coupon.description && (
        <p className="mt-0.5 line-clamp-2 text-xs text-muted">{coupon.description}</p>
      )}

      {/* Code + copy */}
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="rounded-md border border-success/70 px-3 py-1.5 font-mono text-xs font-bold uppercase tracking-wider text-success">
          {coupon.code}
        </span>
        <button
          type="button"
          onClick={copyCode}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-success transition-opacity hover:opacity-80"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {coupon.minOrderValue > 0 && (
        <p className="mt-2.5 text-xs text-muted">Min order {formatCurrency(coupon.minOrderValue)}</p>
      )}
    </div>
  );
}

export default HomePage;
